import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import type { LinhaImportada, PreviewImportacao, SecaoCalculada } from "../../../shared/tipos";
import { api } from "../api/cliente";
import { chaves } from "../api/consultas";
import { useAvisos } from "../components/Avisos";
import { formatarMoeda, formatarQuantidade, Tabela } from "../components/ui";

type Destino = "item" | "cotacao" | "ignorar";

const destinoInicial = (l: LinhaImportada): Destino => (l.item ? "item" : "cotacao");

async function lerBase64(arquivo: File): Promise<string> {
  const bytes = new Uint8Array(await arquivo.arrayBuffer());
  let bin = "";
  for (let i = 0; i < bytes.length; i += 8192) bin += String.fromCharCode(...bytes.subarray(i, i + 8192));
  return btoa(bin);
}

/** Anexar uma planilha de quantitativos (.xlsx): casa os itens com a tabela e monta o orçamento. */
export function ImportarQuantitativos({
  orcamentoId,
  secaoAtiva = null,
  secoes = [],
}: {
  orcamentoId: number;
  secaoAtiva?: number | null;
  secoes?: SecaoCalculada[];
}) {
  const [preview, setPreview] = useState<PreviewImportacao | null>(null);
  const [destinos, setDestinos] = useState<Destino[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { avisar, avisarErro } = useAvisos();

  const enviar = useMutation({
    mutationFn: async (arquivo: File) => {
      const base64 = await lerBase64(arquivo);
      return api<PreviewImportacao>(`/api/orcamentos/${orcamentoId}/importar`, {
        method: "POST",
        body: { nome: arquivo.name, base64 },
      });
    },
    onSuccess: (p) => {
      setPreview(p);
      setDestinos(p.linhas.map(destinoInicial));
    },
    onError: avisarErro,
  });

  const aplicar = useMutation({
    mutationFn: () => {
      const linhas = preview!.linhas;
      const itens = linhas
        .filter((l, i) => destinos[i] === "item" && l.item)
        .map((l) => ({ item_id: l.item!.id, quantidade: l.quantidade }));
      const cotacao = linhas
        .filter((_, i) => destinos[i] === "cotacao")
        .map((l) => ({ codigo_planilha: l.codigo_planilha, descricao: l.descricao_planilha, unidade: l.unidade_planilha, quantidade: l.quantidade }));
      return api<{ mensagem: string }>(`/api/orcamentos/${orcamentoId}/importar/aplicar`, { method: "POST", body: { itens, cotacao, secao_id: secaoAtiva } });
    },
    onSuccess: (r) => {
      avisar(r.mensagem);
      setPreview(null);
      setDestinos([]);
      if (inputRef.current) inputRef.current.value = "";
      queryClient.invalidateQueries({ queryKey: chaves.orcamento(orcamentoId) });
      queryClient.invalidateQueries({ queryKey: chaves.painel });
    },
    onError: avisarErro,
  });

  const contarDestino = (d: Destino) => destinos.filter((x) => x === d).length;
  const contarStatus = (s: LinhaImportada["status"]) => preview?.linhas.filter((l) => l.status === s).length ?? 0;
  const temPrecoAntigo = preview?.linhas.some((l) => l.preco_planilha !== null) ?? false;
  const variacao = (l: LinhaImportada): number | null =>
    l.item && l.preco_planilha && l.preco_planilha > 0 ? ((l.item.preco_unitario - l.preco_planilha) / l.preco_planilha) * 100 : null;
  const mudaram = preview?.linhas.filter((l) => {
    const v = variacao(l);
    return v !== null && Math.abs(v) >= 0.01;
  }).length ?? 0;

  return (
    <details className="card no-print" style={{ marginBottom: 18 }}>
      <summary className="btn btn-orange" style={{ padding: "12px 22px", fontSize: "0.95rem" }}>
        Importar planilha de quantitativos (Excel)
      </summary>
      <div style={{ marginTop: 14 }}>
        <p className="note">
          Anexe um Excel (.xlsx) com as colunas <strong>Descrição</strong> (ou <strong>Código</strong>) e{" "}
          <strong>Quantidade</strong>. O sistema procura cada item na tabela deste orçamento e monta a planilha; o que não
          for encontrado vai para a <strong>lista de cotação</strong>. Se a planilha for um{" "}
          <strong>orçamento antigo</strong> com uma coluna de preço/valor unitário, o preço é atualizado para o da tabela
          atual e a variação é mostrada.
        </p>
        <div className="form-row">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={enviar.isPending}
            onChange={(e) => {
              const arquivo = e.target.files?.[0];
              if (arquivo) enviar.mutate(arquivo);
            }}
          />
          {enviar.isPending && <span className="note">Lendo a planilha…</span>}
        </div>

        {preview && (
          <>
            <p className="note" style={{ marginTop: 8 }}>
              <strong>{preview.arquivo}</strong> — {preview.total_linhas} linha(s): {contarStatus("codigo")} por código,{" "}
              {contarStatus("descricao")} por descrição, {contarStatus("nao_encontrado")} não encontrado(s)
              {temPrecoAntigo ? ` · ${mudaram} com preço diferente da tabela atual` : ""}. Confira o destino de cada linha
              antes de aplicar.
              {secaoAtiva && secoes.length > 0 && (
                <>
                  {" "}
                  Os itens entram na seção <strong>{secoes.find((sc) => sc.id === secaoAtiva)?.numero} {secoes.find((sc) => sc.id === secaoAtiva)?.titulo}</strong>.
                </>
              )}
            </p>
            <Tabela>
              <thead>
                <tr>
                  <th>Linha da planilha</th>
                  <th>Item na tabela EMBASA</th>
                  <th className="num">Qtd.</th>
                  {temPrecoAntigo && <th className="num">Preço planilha</th>}
                  <th className="num">Preço atual</th>
                  {temPrecoAntigo && <th className="num">Variação</th>}
                  <th>Destino</th>
                </tr>
              </thead>
              <tbody>
                {preview.linhas.map((l, i) => (
                  <tr key={i} className={destinos[i] === "ignorar" ? "canteiro-linha-fora" : undefined}>
                    <td>
                      {l.codigo_planilha && <strong>{l.codigo_planilha} </strong>}
                      {l.descricao_planilha}
                      {l.unidade_planilha && <span className="note"> · {l.unidade_planilha}</span>}
                    </td>
                    <td>
                      {l.item ? (
                        <>
                          <strong>{l.item.codigo}</strong> {l.item.descricao}
                          {l.status === "descricao" && (
                            <span className="badge badge-warn" title="Casado pela descrição — confira se é o item certo.">
                              por descrição
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="badge badge-muted">não encontrado na tabela</span>
                      )}
                    </td>
                    <td className="num">{formatarQuantidade(l.quantidade)}</td>
                    {temPrecoAntigo && <td className="num">{l.preco_planilha !== null ? formatarMoeda(l.preco_planilha) : "—"}</td>}
                    <td className="num">{l.item ? formatarMoeda(l.item.preco_unitario) : "—"}</td>
                    {temPrecoAntigo && (
                      <td className="num">
                        {(() => {
                          const v = variacao(l);
                          if (v === null) return "—";
                          const sinal = v > 0 ? "+" : "";
                          return (
                            <span className={`badge ${Math.abs(v) < 0.01 ? "badge-muted" : v > 0 ? "badge-warn" : "badge-green"}`}>
                              {sinal}
                              {formatarQuantidade(Math.round(v * 10) / 10)}%
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    <td>
                      <select
                        value={destinos[i]}
                        onChange={(e) => setDestinos((atual) => atual.map((d, j) => (j === i ? (e.target.value as Destino) : d)))}
                      >
                        {l.item && <option value="item">Adicionar ao orçamento</option>}
                        <option value="cotacao">Enviar para cotação</option>
                        <option value="ignorar">Ignorar</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Tabela>
            <div className="canteiro-acoes">
              <span>
                {contarDestino("item")} item(ns) para o orçamento · {contarDestino("cotacao")} para cotação
              </span>
              <button type="button" className="btn-ghost" onClick={() => setPreview(null)} disabled={aplicar.isPending}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn-orange"
                disabled={aplicar.isPending || contarDestino("item") + contarDestino("cotacao") === 0}
                onClick={() => aplicar.mutate()}
              >
                {aplicar.isPending ? "Aplicando…" : "Aplicar ao orçamento"}
              </button>
            </div>
          </>
        )}
      </div>
    </details>
  );
}
